import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Save, Clock } from 'lucide-react';
import canvassingMobileService from '../../services/canvassingMobileService';

const NotesSection = ({ leadId, initialNotes = [] }) => {
  const [noteText, setNoteText] = useState('');
  const [noteId, setNoteId] = useState(null);
  const [notes, setNotes] = useState(initialNotes);
  const [saveStatus, setSaveStatus] = useState(''); // '', 'saving', 'saved'
  const saveTimer = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Reset when lead changes
  useEffect(() => {
    setNoteText('');
    setNoteId(null);
    setNotes(initialNotes);
    setSaveStatus('');
  }, [leadId, initialNotes]);

  // Auto-save debounce (2 seconds after last keystroke)
  const debouncedSave = useCallback((text, currentNoteId) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      if (!text.trim() || !isMounted.current) return;
      setSaveStatus('saving');
      try {
        if (currentNoteId) {
          await canvassingMobileService.updateNote(currentNoteId, text.trim());
        } else {
          const result = await canvassingMobileService.createNote(leadId, text.trim());
          if (isMounted.current && result.noteId) {
            setNoteId(result.noteId);
          }
        }
        if (isMounted.current) setSaveStatus('saved');
      } catch (err) {
        console.error('Auto-save failed:', err);
        if (isMounted.current) setSaveStatus('');
      }
    }, 2000);
  }, [leadId]);

  const handleTextChange = (text) => {
    setNoteText(text);
    setSaveStatus('');
    debouncedSave(text, noteId);
  };

  // Save immediately on blur (user tapping away)
  const handleBlur = async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (!noteText.trim()) return;
    setSaveStatus('saving');
    try {
      if (noteId) {
        await canvassingMobileService.updateNote(noteId, noteText.trim());
      } else {
        const result = await canvassingMobileService.createNote(leadId, noteText.trim());
        if (isMounted.current && result.noteId) {
          setNoteId(result.noteId);
        }
      }
      if (isMounted.current) setSaveStatus('saved');
    } catch (err) {
      console.error('Save on blur failed:', err);
    }
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const day = d.getDate();
    const suffix = [11,12,13].includes(day) ? 'th' : {1:'st',2:'nd',3:'rd'}[day%10] || 'th';
    const weekday = d.toLocaleDateString('en-GB', { weekday: 'long' });
    const month = d.toLocaleDateString('en-GB', { month: 'short' });
    const year = d.getFullYear();
    const time = d.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
    return `${weekday} ${day}${suffix} ${month} ${year}, ${time}`;
  };

  return (
    <div className="space-y-2">
      {/* Current note input */}
      <div className="relative">
        <textarea
          value={noteText}
          onChange={e => handleTextChange(e.target.value)}
          onBlur={handleBlur}
          placeholder="Type notes here... (auto-saves)"
          rows={3}
          className="w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base p-3 pr-16"
        />
        {saveStatus && (
          <span className={`absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full
            ${saveStatus === 'saving' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
            {saveStatus === 'saving' ? (
              <span className="flex items-center gap-1"><Save size={10} /> Saving...</span>
            ) : (
              <span className="flex items-center gap-1"><Save size={10} /> Saved</span>
            )}
          </span>
        )}
      </div>

      {/* Previous timestamped notes */}
      {notes.length > 0 && (
        <div className="space-y-1.5">
          {notes.map(note => (
            <div key={note.id} className="bg-gray-50 rounded-lg px-3 py-2 text-sm">
              <div className="flex items-center gap-1 text-xs text-gray-400 mb-0.5">
                <Clock size={10} />
                <span>{formatTime(note.created_at)}</span>
                {note.user_name && <span>· {note.user_name}</span>}
              </div>
              <p className="text-gray-700">{note.note_text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotesSection;
